// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// D-CRX-87 §4d safety net 1 — e-sign failures in words. A raw server code (`docusign_not_configured`,
// `docusign_disabled_for_pharmacy`, …) never reaches the screen: known codes get words written for a pharmacy,
// anything else falls back to friendlyError (which never shows a code either). D-CRX-29 T7.

import { friendlyError } from './errors'
import { SIGNING_WORDS } from './inappEsign'

export const ESIGN_CODE_WORDS: Readonly<Record<string, string>> = {
  docusign_not_configured: `${SIGNING_WORDS.docusignNotSetUp}. Ask your administrator to connect DocuSign before sending for signature.`,
  docusign_disabled_for_pharmacy: SIGNING_WORDS.inAppOnly,
  inapp_signing_off: 'In-app signing is not turned on for this pharmacy. Ask your administrator to change Signing in the SaaS-admin CareRx tab.',
  inapp_not_available_for_document: 'Only the enrollment form can be signed in OwlRx for now. Send this document with DocuSign.',
  docusign_send_failed: 'DocuSign did not accept the envelope. Try again in a moment; if it keeps failing, tell the OwlRx team.',
  consent_envelope_already_open: 'A consent envelope is already out for this therapy. Check or void it before sending another.',
  enrollment_envelope_already_open: 'An enrollment envelope is already out for this therapy. Check or void it before sending another.',
  envelope_already_open: 'This document already has an open signature request (someone may have just started one). Open it from the tracker, or void it before starting another.',
  signing_busy: 'Another signing attempt for this signature is still being checked. Try again in a moment.',
  enrollment_incomplete: 'This form becomes ready to send when clinical review completes.',
  clinical_review_incomplete: 'This enrollment has not completed clinical review yet. Send it for signature once the review is approved.',
  patient_email_missing: 'There is no email for this patient. Enter one to send.',
  patient_email_invalid: 'That patient email address is not valid.',
  interpreter_email_invalid: 'That interpreter email address is not valid.',
  provider_email_missing: 'There is no email on the prescriber record. Enter the provider email to send.',
  provider_email_invalid: 'That provider email address is not valid.',
  pharmacist_email_missing: 'The assigned pharmacist has no email. Assign a pharmacist or enter their email to send.',
  pharmacist_email_invalid: 'That pharmacist email address is not valid.',
  recipient_email_required: 'Enter the physician email to send for signature.',
  recipient_email_invalid: 'That email address is not valid.',
  recipient_name_required: 'Enter the physician name to send for signature.',
  tenant_state_unset_jurisdiction_unresolved: 'The pharmacy’s state is not set on its profile, so the consent’s state disclosures cannot be chosen. Set the state, then send again.',
  envelope_not_found: 'That envelope could not be found. Refresh the page.',
  document_not_rendered: 'Generate the document before sending it for signature.',
}

/** The words for an e-sign failure: a known code's words, else friendlyError. Never the raw code. */
export function esignErrorText(e: unknown): string {
  const code = (e as { body?: { error?: unknown } } | null)?.body?.error
  if (typeof code === 'string' && Object.prototype.hasOwnProperty.call(ESIGN_CODE_WORDS, code)) return ESIGN_CODE_WORDS[code]
  return friendlyError(e)
}
